import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import {
  customObjectsService,
  type CustomObjectType,
  type CustomObjectRecord,
  type MediaItem,
} from '@/services/custom-objects.service';
import { leadsService, type Lead } from '@/services/leads.service';
import { Search, ArrowLeft, Database, ShoppingBag, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';

const IMAGE_URL_RE = /\.(jpe?g|png|webp|gif)(\?.*)?$/i;

function isImageUrl(v: unknown): v is string {
  return typeof v === 'string'
    && /^https?:\/\//i.test(v)
    && (IMAGE_URL_RE.test(v) || v.includes('ik.imagekit.io'));
}

/**
 * Find an image to attach: prefer the first image item of a `media` field,
 * fall back to any url-typed field whose value looks like an image URL.
 */
function findImage(type: CustomObjectType, record: CustomObjectRecord): string | undefined {
  for (const f of type.fields) {
    if (f.type !== 'media') continue;
    const items = record.values?.[f.key];
    if (Array.isArray(items)) {
      const img = (items as MediaItem[]).find((i) => i?.kind === 'image' && typeof i.url === 'string');
      if (img) return img.url;
    }
  }
  for (const f of type.fields) {
    if (f.type !== 'url') continue;
    const v = record.values?.[f.key];
    if (isImageUrl(v)) return v;
  }
  return undefined;
}

function formatMoney(v: unknown, currency: string): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? '');
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${currency} ${n.toLocaleString()}`;
  }
}

/**
 * Compose the WhatsApp message body: bold title line, then up to 5
 * AI-context fields as "Label: value" lines. Media fields are skipped
 * (the image goes out as the attachment, not as URLs in the text).
 */
function buildMessage(type: CustomObjectType, record: CustomObjectRecord, currency: string): string {
  const lines: string[] = [];
  const title = record.title?.trim();
  if (title) lines.push(`*${title}*`);

  const fields = [...type.fields]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter((f) => f.includeInAiContext && f.type !== 'media');

  let added = 0;
  for (const f of fields) {
    if (added >= 5) break;
    const v = record.values?.[f.key];
    if (v === undefined || v === null || v === '') continue;
    let rendered: string;
    if (f.type === 'money') rendered = formatMoney(v, currency);
    else if (typeof v === 'boolean') rendered = v ? 'Yes' : 'No';
    else if (Array.isArray(v)) rendered = v.join(', ');
    else rendered = String(v);
    // Don't repeat the title as a field line.
    if (title && rendered === title) continue;
    lines.push(`${f.label}: ${rendered}`);
    added++;
  }
  return lines.join('\n');
}

interface Props {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  /** Called after a successful send so the parent can reload the thread. */
  onSent: () => void;
}

/**
 * Catalog picker for the chat composer: browse custom object records
 * (menu items, services, listings…) and send one as a formatted WhatsApp
 * message — with the record's photo attached when one exists. Uses the
 * free-form reply endpoint, so it's only available inside the 24h window
 * (the composer disables the trigger button outside it).
 */
export function CatalogPickerModal({ open, onClose, lead, onSent }: Props) {
  const companyCurrency = useCompanyCurrency();
  const [types, setTypes] = useState<CustomObjectType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedType, setSelectedType] = useState<CustomObjectType | null>(null);

  const [records, setRecords] = useState<CustomObjectRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<CustomObjectRecord | null>(null);
  const [sending, setSending] = useState(false);

  const currency = lead.estimatedValueCurrency ?? companyCurrency;

  useEffect(() => {
    if (!open) return;
    setSelectedType(null);
    setSelectedRecord(null);
    setSearch('');
    setLoadingTypes(true);
    customObjectsService
      .listTypes(false)
      .then(setTypes)
      .catch(() => { /* interceptor surfaces */ })
      .finally(() => setLoadingTypes(false));
  }, [open]);

  // Debounced record search whenever a type is selected or the query changes.
  useEffect(() => {
    if (!open || !selectedType) return;
    let cancelled = false;
    setLoadingRecords(true);
    const t = window.setTimeout(() => {
      customObjectsService
        .listRecords({ typeKey: selectedType.key, q: search.trim() || undefined, limit: 50 })
        .then((r) => { if (!cancelled) setRecords(r.items); })
        .catch(() => { /* interceptor surfaces */ })
        .finally(() => { if (!cancelled) setLoadingRecords(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [open, selectedType, search]);

  const message = useMemo(
    () => (selectedType && selectedRecord ? buildMessage(selectedType, selectedRecord, currency) : ''),
    [selectedType, selectedRecord, currency],
  );
  const imageUrl = useMemo(
    () => (selectedType && selectedRecord ? findImage(selectedType, selectedRecord) : undefined),
    [selectedType, selectedRecord],
  );

  const send = async () => {
    if (!selectedRecord || !message) return;
    setSending(true);
    try {
      const r = await leadsService.reply(lead.leadUid, imageUrl
        ? { body: message, mediaUrl: imageUrl, mediaType: 'image' }
        : { body: message });
      if (r.ok) {
        toast.success('Sent');
        onSent();
        onClose();
      } else {
        toast.error(r.error === 'OUTSIDE_24H_WINDOW'
          ? 'The 24-hour reply window has closed — send an approved template instead.'
          : (r.error ?? 'Send failed'));
      }
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Send from catalog" widthClass="max-w-xl">
      {/* Step 1 — pick a type */}
      {!selectedType && (
        <div className="space-y-1.5">
          {loadingTypes && [0, 1, 2].map((i) => <div key={i} className="skeleton h-14 rounded-lg" />)}
          {!loadingTypes && types.length === 0 && (
            <p className="py-8 text-center text-sm text-text-muted">
              No custom object types yet — set them up under Custom Objects.
            </p>
          )}
          {!loadingTypes && types.map((t) => (
            <button
              key={t.key}
              onClick={() => { setSelectedType(t); setSearch(''); setSelectedRecord(null); }}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-surface-alt"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                <Database size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-text-primary">{t.pluralLabel ?? t.label}</div>
                {t.description && <div className="truncate text-xs text-text-muted">{t.description}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2 — pick a record */}
      {selectedType && !selectedRecord && (
        <div className="space-y-3">
          <button
            onClick={() => setSelectedType(null)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-primary"
          >
            <ArrowLeft size={12} /> All types
          </button>
          <Input
            placeholder={`Search ${selectedType.pluralLabel ?? selectedType.label}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            iconLeft={<Search size={14} />}
          />
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {loadingRecords && [0, 1, 2].map((i) => <div key={i} className="skeleton h-14 rounded-lg" />)}
            {!loadingRecords && records.length === 0 && (
              <p className="py-8 text-center text-sm text-text-muted">
                No records{search ? ' match your search' : ' yet'}.
              </p>
            )}
            {!loadingRecords && records.map((r) => {
              const img = findImage(selectedType, r);
              const previewFields = selectedType.fields
                .filter((f) => f.includeInAiContext && f.type !== 'media')
                .slice(0, 2);
              return (
                <button
                  key={r.recordUid}
                  onClick={() => setSelectedRecord(r)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 text-left transition-colors hover:border-primary/50 hover:bg-surface-alt"
                >
                  {img ? (
                    <img src={img} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-muted">
                      <ShoppingBag size={14} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-text-primary">
                      {r.title || 'untitled'}
                    </div>
                    {previewFields.length > 0 && (
                      <div className="truncate text-xs text-text-muted">
                        {previewFields
                          .map((f) => {
                            const v = r.values?.[f.key];
                            if (v === undefined || v === null || v === '') return null;
                            return f.type === 'money' ? formatMoney(v, currency) : String(v);
                          })
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3 — preview + send */}
      {selectedType && selectedRecord && (
        <div className="space-y-4">
          <button
            onClick={() => setSelectedRecord(null)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-primary"
          >
            <ArrowLeft size={12} /> Choose a different record
          </button>

          <div>
            <p className="mb-1.5 text-xs font-semibold text-text-primary">Preview</p>
            <div className="rounded-2xl rounded-br-sm bg-primary-soft px-3.5 py-2.5 text-[13.5px] leading-snug text-primary">
              {imageUrl && (
                <img src={imageUrl} alt="" className="mb-2 max-h-44 w-full rounded-lg object-cover" />
              )}
              <div className="whitespace-pre-wrap break-words">
                {/* Mirror WhatsApp's *bold* rendering for the title line */}
                {message.split('\n').map((line, i) => (
                  <p key={i} className={i === 0 && line.startsWith('*') ? 'font-semibold' : undefined}>
                    {line.replace(/^\*(.*)\*$/, '$1')}
                  </p>
                ))}
              </div>
            </div>
            {imageUrl && (
              <p className="mt-1 inline-flex items-center gap-1 text-2xs text-text-muted">
                <ImageIcon size={10} /> Sent as an image with the text as caption
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button variant="primary" onClick={send} loading={sending} disabled={sending || !message}>
              Send to {lead.profileName?.trim() || lead.phone}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
